import api from "./axios";
import { type User, mapUserResponse } from "../types/user";
import axios from "axios";
import { store } from "../store";
import { isErrorResponse } from "../utils/errorHandling";
import i18n from "../translations/i18n";

// Create a special instance for password reset that doesn't handle auth errors
const passwordResetApi = axios.create({
  baseURL: api.defaults.baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add the same request interceptor as the main api instance
passwordResetApi.interceptors.request.use(
  (config) => {
    const token = store.getState().user.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Set Accept-Language header based on selected language
    const language = i18n.language || localStorage.getItem("language") || "id";
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Don't add auth interceptors to this instance
passwordResetApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle non-401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

export interface UserResponse {
  data: {
    user: User;
  };
}

export interface GetCurrentUserResponse {
  user: User;
}

export const getCurrentUserApi = async (): Promise<GetCurrentUserResponse> => {
  try {
    const response = await api.get<any>("/api/v1/users/whoami");
    return {
      user: mapUserResponse(response.data.data.user),
    };
  } catch (error) {
    console.error("Error fetching current user:", error);
    throw error;
  }
};

export interface UsersResponse {
  data: {
    users: User[];
  };
}

export interface CreateUserData {
  _id: string;
  name: string;
  role: string;
  password: string;
}

export const getUsersApi = async (): Promise<User[]> => {
  try {
    const response = await api.get<UsersResponse>("/api/v1/users");
    return response.data.data.users.map((user) => mapUserResponse(user));
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const createUserApi = async (userData: CreateUserData): Promise<void> => {
  try {
    await api.post("/api/v1/users", userData);
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const resetUserPasswordApi = async (userId: string, newPassword: string): Promise<void> => {
  try {
    await api.post(`/api/v1/users/${userId}/adminResetPassword`, {
      _id: userId,
      newPassword,
    });
  } catch (error) {
    console.error("Error resetting user password:", error);
    throw error;
  }
};

export interface LoginResponse {
  data: {
    token: string;
    user: User;
  };
}

export const loginApi = async (formData: {
  id: string;
  password: string;
}): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>("/api/v1/users/login", formData);
    return response.data;
  } catch (error: any) {
    if (isErrorResponse(error)) {
      throw error;
    }
    throw error;
  }
};

export interface RefreshTokenResponse {
  data: {
    token: string;
    user: User;
    refreshToken?: string;
  };
}

export const refreshTokenApi = async (): Promise<RefreshTokenResponse> => {
  try {
    const response = await api.post<any>("/api/v1/users/refresh");
    return {
      data: {
        token: response.data.data.token,
        user: mapUserResponse(response.data.data.user),
        refreshToken: response.data.data.refreshToken,
      },
    };
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

export const logoutApi = async (): Promise<void> => {
  try {
    await api.post("/api/v1/users/logout");
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

export interface ResetPasswordData {
  _id: string;
  password: string;
  newPassword: string;
}

export class WrongPasswordError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WrongPasswordError";
  }
}

export const resetPasswordApi = async (userId: string, data: ResetPasswordData): Promise<void> => {
  try {
    await passwordResetApi.post(`/api/v1/users/${userId}/resetPassword`, {
      ...data,
      _id: userId,
    });
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.data?.message?.includes("password")) {
      throw new WrongPasswordError(error.response.data.message);
    }
    throw error;
  }
};
